import { useState, type ReactNode } from 'react';
import {
  Bar, BarChart, CartesianGrid, Cell, LabelList,
  PolarAngleAxis, PolarGrid, PolarRadiusAxis, Radar, RadarChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import type { ComplianceBand, ScoreChartStyle, SectionScore } from '../../../lib/api/queries';
import { BAND_COLORS, bandLabel } from './bands';

/**
 * Compliance by section, four ways. Every style plots the same numbers
 * (each section's % on 0–100); clients read charts differently, so the
 * Results tab lets staff flip between them and the Report tab picks the
 * one the published report uses. The PDF draws the same four
 * (DPAS.AuditTool.Infrastructure/Services/ScoreCharts); keep them in step.
 */

export const SCORE_CHART_OPTIONS: { style: ScoreChartStyle; label: string; hint: string }[] = [
  { style: 'Radar',   label: 'Radar',   hint: 'One spoke per section' },
  { style: 'Bar',     label: 'Bar',     hint: 'A bar per section' },
  { style: 'Stacked', label: 'Stacked', hint: 'Achieved against the remaining gap' },
  { style: 'Circle',  label: 'Circle',  hint: 'A ring per section' },
];

type ChartRow = {
  code: string;
  name: string;
  percent: number;
  band: ComplianceBand;
  colour: string;
  /** "76%", or "N/A" when nothing in the section is scored. */
  label: string;
};

function toRows(sections: SectionScore[]): ChartRow[] {
  return sections.map(s => {
    const percent = Math.round(Math.min(Math.max(s.percentage, 0), 1) * 100);
    return {
      code: s.code.replace(/\.0$/, ''),
      name: s.name,
      percent,
      band: s.band,
      colour: BAND_COLORS[s.band],
      label: s.band === 'NotApplicable' ? 'N/A' : `${percent}%`,
    };
  });
}

/** The chart for one style. Plot only; the caller supplies the card. */
export function ScoreChart({ style, sections }: { style: ScoreChartStyle; sections: SectionScore[] }) {
  if (sections.length === 0) {
    return <p className="text-sm text-gray-500">No sections to chart yet.</p>;
  }
  switch (style) {
    case 'Bar':     return <SectionBars sections={sections} />;
    case 'Stacked': return <SectionStacked sections={sections} />;
    case 'Circle':  return <SectionRings sections={sections} />;
    default:        return <SectionRadar sections={sections} />;
  }
}

/** Segmented tabs for choosing a chart style. */
export function ScoreChartTabs({
  value, onChange, disabled = false, label,
}: {
  value: ScoreChartStyle;
  onChange: (style: ScoreChartStyle) => void;
  disabled?: boolean;
  /** Accessible name for the tab list. */
  label: string;
}) {
  return (
    <div role="tablist" aria-label={label}
         className="inline-flex rounded-md border border-gray-200 bg-gray-100 p-0.5">
      {SCORE_CHART_OPTIONS.map(option => {
        const selected = option.style === value;
        return (
          <button key={option.style} type="button" role="tab"
                  aria-selected={selected}
                  title={option.hint}
                  disabled={disabled && !selected}
                  onClick={() => { if (!selected) onChange(option.style); }}
                  className={
                    'px-3 py-1 text-sm rounded transition-colors disabled:cursor-not-allowed ' +
                    (selected
                      ? 'bg-white text-gray-900 font-medium shadow-sm'
                      : 'text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:hover:text-gray-600')
                  }>
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/** The white card every chart sits in: title and tabs on top, chart, then an optional note. */
export function ScoreChartCard({
  title, controls, note, children,
}: {
  title: string;
  controls?: ReactNode;
  note?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <header className="px-5 py-3 border-b border-gray-200 bg-gray-50
                         flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        {controls}
      </header>
      <div className="p-4">{children}</div>
      {note && <footer className="px-5 pb-4 -mt-1 text-xs text-gray-500">{note}</footer>}
    </section>
  );
}

const RESULTS_CHART_KEY = 'dpas.results.scoreChart';

function readResultsChart(): ScoreChartStyle {
  try {
    const stored = window.localStorage.getItem(RESULTS_CHART_KEY);
    return SCORE_CHART_OPTIONS.some(o => o.style === stored) ? stored as ScoreChartStyle : 'Radar';
  } catch {
    return 'Radar';
  }
}

function writeResultsChart(style: ScoreChartStyle): void {
  try {
    window.localStorage.setItem(RESULTS_CHART_KEY, style);
  } catch {
    // Private mode or blocked storage: the tab just won't be remembered.
  }
}

/**
 * The Results tab's chart with its style tabs. The choice is only a view
 * preference (remembered in this browser); the report's chart is chosen
 * on the Report tab.
 */
export function ResultsScoreChart({ sections }: { sections: SectionScore[] }) {
  const [style, setStyle] = useState<ScoreChartStyle>(readResultsChart);
  const choose = (next: ScoreChartStyle) => { setStyle(next); writeResultsChart(next); };

  return (
    <ScoreChartCard
      title="Compliance by section"
      controls={<ScoreChartTabs value={style} onChange={choose} label="Chart style" />}>
      <ScoreChart style={style} sections={sections} />
    </ScoreChartCard>
  );
}

// ---------------------------------------------------------------------------
//  The four charts
// ---------------------------------------------------------------------------

/** Hover card shared by the bar chart: code, name, % and band. */
function RowTooltip({ row }: { row: ChartRow }) {
  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-xs shadow-sm">
      <p className="font-semibold text-gray-900">{row.code} — {row.name}</p>
      <p className="text-gray-600 mt-0.5">{row.label} · {bandLabel(row.band)}</p>
    </div>
  );
}

/** Which band each colour means, for the charts coloured by band. */
function BandLegend({ rows }: { rows: ChartRow[] }) {
  const order: ComplianceBand[] = ['VeryHigh', 'High', 'Medium', 'Low', 'VeryLow', 'NotApplicable'];
  const present = order.filter(band => rows.some(r => r.band === band));
  return (
    <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600" aria-label="Compliance bands">
      {present.map(band => (
        <li key={band} className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: BAND_COLORS[band] }} aria-hidden />
          {bandLabel(band)}
        </li>
      ))}
    </ul>
  );
}

/**
 * Radar mirroring the spreadsheet's "Org Assessment Results" tab. Each
 * spoke is a section; the ring shows the audit's % compliance per section
 * on a 0-100 scale. Sections sit at uniform angles regardless of how many
 * there are.
 */
export function SectionRadar({ sections }: { sections: SectionScore[] }) {
  const data = toRows(sections);
  return (
    <div style={{ height: 460 }}>
      <ResponsiveContainer width="100%" height="100%">
        <RadarChart data={data} outerRadius="62%">
          <PolarGrid stroke="#e5e7eb" />
          <PolarAngleAxis dataKey="name" tick={renderWrappedTick} />
          <PolarRadiusAxis
            angle={90}
            domain={[0, 100]}
            tick={{ fill: '#9ca3af', fontSize: 10 }}
            tickFormatter={v => `${v}%`}
          />
          <Radar dataKey="percent" stroke="#C84A99" fill="#C84A99" fillOpacity={0.35} />
          <Tooltip
            // Recharts widened the formatter's first arg — coerce back to a
            // number (the tooltip only fires on rows with a numeric percent).
            formatter={v => [`${Number(v ?? 0)}%`, 'Compliance']}
            labelFormatter={(_, payload) => {
              const item = payload?.[0]?.payload as ChartRow | undefined;
              return item ? `${item.code} — ${item.name}` : '';
            }}
            contentStyle={{ fontSize: 12 }}
          />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
}

/** A vertical bar per section, coloured by its band, % above each bar. */
function SectionBars({ sections }: { sections: SectionScore[] }) {
  const data = toRows(sections);
  return (
    <>
      <div style={{ height: 360 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 24, right: 8, bottom: 4, left: 0 }}>
            <CartesianGrid vertical={false} stroke="#e5e7eb" />
            <XAxis dataKey="code" tickLine={false}
                   axisLine={{ stroke: '#9ca3af' }}
                   tick={{ fill: '#374151', fontSize: 11 }} />
            <YAxis domain={[0, 100]} ticks={[0, 20, 40, 60, 80, 100]} width={40}
                   axisLine={false} tickLine={false}
                   tick={{ fill: '#9ca3af', fontSize: 10 }}
                   tickFormatter={v => `${v}%`} />
            <Tooltip
              cursor={{ fill: '#f3f4f6' }}
              content={({ active, payload }) =>
                active && payload?.length
                  ? <RowTooltip row={payload[0].payload as ChartRow} />
                  : null}
            />
            <Bar dataKey="percent" radius={[4, 4, 0, 0]} maxBarSize={48} isAnimationActive={false}>
              {data.map(row => <Cell key={row.code} fill={row.colour} />)}
              <LabelList dataKey="label" position="top" style={{ fill: '#374151', fontSize: 11 }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <BandLegend rows={data} />
    </>
  );
}

/** A 0–100% bar per section: achieved in its band colour, the remaining gap in grey. */
function SectionStacked({ sections }: { sections: SectionScore[] }) {
  const data = toRows(sections);
  return (
    <>
      <ul className="space-y-2">
        {data.map(row => (
          <li key={row.code}
              title={`${row.code} — ${row.name}: ${row.label} (${bandLabel(row.band)})`}
              className="grid grid-cols-[minmax(10rem,18rem)_1fr_3rem] items-center gap-3 text-sm">
            <span className="truncate text-gray-800">
              <span className="font-semibold text-gray-900">{row.code}</span> {row.name}
            </span>
            <span className="flex h-3 w-full" aria-hidden>
              {row.percent > 0 && (
                <span className="h-full rounded-l"
                      style={{
                        width: `${row.percent}%`,
                        backgroundColor: row.colour,
                        borderTopRightRadius: row.percent === 100 ? 4 : 0,
                        borderBottomRightRadius: row.percent === 100 ? 4 : 0,
                      }} />
              )}
              {row.percent < 100 && (
                // 2px surface gap where achieved meets remaining.
                <span className={'h-full flex-1 bg-gray-200 rounded-r ' + (row.percent > 0 ? 'ml-0.5' : 'rounded-l')} />
              )}
            </span>
            <span className="text-right tabular-nums text-gray-700">{row.label}</span>
          </li>
        ))}
      </ul>
      <div className="grid grid-cols-[minmax(10rem,18rem)_1fr_3rem] gap-3 mt-1 text-[11px] text-gray-400" aria-hidden>
        <span />
        <span className="flex justify-between"><span>0%</span><span>50%</span><span>100%</span></span>
        <span />
      </div>
      <BandLegend rows={data} />
    </>
  );
}

const RING_RADIUS = 34;
const RING_STROKE = 10;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;

/** A ring gauge per section, filled to its % in its band colour. */
function SectionRings({ sections }: { sections: SectionScore[] }) {
  const data = toRows(sections);
  const size = (RING_RADIUS + RING_STROKE) * 2;
  return (
    <>
      <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-6 gap-x-4 gap-y-6">
        {data.map(row => (
          <li key={row.code}
              title={`${row.code} — ${row.name}: ${row.label} (${bandLabel(row.band)})`}
              className="flex flex-col items-center text-center">
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img"
                 aria-label={`${row.code} ${row.name}: ${row.label}`}>
              <circle cx={size / 2} cy={size / 2} r={RING_RADIUS}
                      fill="none" stroke="#e5e7eb" strokeWidth={RING_STROKE} />
              {row.percent > 0 && (
                <circle cx={size / 2} cy={size / 2} r={RING_RADIUS}
                        fill="none" stroke={row.colour} strokeWidth={RING_STROKE}
                        strokeLinecap={row.percent < 100 ? 'round' : 'butt'}
                        strokeDasharray={`${(RING_LENGTH * row.percent) / 100} ${RING_LENGTH}`}
                        transform={`rotate(-90 ${size / 2} ${size / 2})`} />
              )}
              <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central"
                    fontSize={16} fontWeight={600} fill="#111827">
                {row.label}
              </text>
            </svg>
            <p className="mt-2 text-xs font-semibold text-gray-900">{row.code}</p>
            <p className="text-xs text-gray-600 leading-snug line-clamp-2 max-w-[11rem]">{row.name}</p>
          </li>
        ))}
      </ul>
      <BandLegend rows={data} />
    </>
  );
}

/**
 * Custom polar-axis tick that wraps long section names onto two lines so
 * spokes like "Subject Access Requests and Individual Rights" don't run off
 * the canvas. Splits on whitespace at the midpoint of the word array.
 */
// Recharts' BaseTickContentProps widens x/y to `string | number` (to
// accommodate category ticks) and its textAnchor is `string`. We accept
// the wider inputs and narrow at the boundary so the SVG <text> gets
// the exact union it expects.
type TickProps = {
  payload?: { value?: unknown };
  x?: string | number;
  y?: string | number;
  cx?: string | number;
  cy?: string | number;
  textAnchor?: string;
};

type SvgTextAnchor = 'start' | 'middle' | 'end' | 'inherit';

const TICK_OFFSET = 18;   // px pushed outward from the radar perimeter

function renderWrappedTick(props: TickProps) {
  const { payload } = props;
  const x  = Number(props.x  ?? 0);
  const y  = Number(props.y  ?? 0);
  const cx = Number(props.cx ?? 0);
  const cy = Number(props.cy ?? 0);
  const rawAnchor = props.textAnchor ?? 'middle';
  const textAnchor: SvgTextAnchor =
    rawAnchor === 'start' || rawAnchor === 'end' || rawAnchor === 'inherit'
      ? rawAnchor
      : 'middle';

  // Push the label outward along the spoke so it sits clear of the radar's
  // outermost ring instead of crowding against it.
  const dx = x - cx;
  const dy = y - cy;
  const dist = Math.hypot(dx, dy) || 1;
  const tx = x + (dx / dist) * TICK_OFFSET;
  const ty = y + (dy / dist) * TICK_OFFSET;

  const raw = String(payload?.value ?? '');
  const words = raw.split(/\s+/);
  let line1 = raw;
  let line2 = '';
  if (words.length > 2) {
    const half = Math.ceil(words.length / 2);
    line1 = words.slice(0, half).join(' ');
    line2 = words.slice(half).join(' ');
  }
  return (
    <g>
      <text x={tx} y={ty} dy={4} textAnchor={textAnchor}
            fill="#374151" fontSize={11}>
        <tspan x={tx} dy="0">{line1}</tspan>
        {line2 && <tspan x={tx} dy="1.1em">{line2}</tspan>}
      </text>
    </g>
  );
}
